import type { ChatPageContext } from './pageContext'

export interface ChatThread {
  cuid: string
  kind: string
  ownerUserId?: string | null
  entityType?: string | null
  entityId?: string | null
  archivedAt?: string | null
  deletedAt?: string | null
  title?: string | null
  createdAt: string
  modifiedAt?: string
}

export interface ChatThreadSummary {
  cuid: string
  title: string
  preview: string
  archivedAt?: string | null
  deletedAt?: string | null
  createdAt: string
  modifiedAt: string
  messageCount: number
  active: boolean
}

export interface ChatMessage {
  cuid: string
  role: string
  body: string
  authorUserId?: string | null
  authorEmail?: string | null
  pageContext?: unknown
  toolsUsed: string[]
  createdAt: string
}

export type ChatStreamHandlers = {
  /** Fired once the POST succeeds — CPS persists the user row before the first SSE byte. */
  onUserPersisted?: () => void
  onThread?: (threadId: string, kind: string) => void
  onDelta?: (text: string) => void
  onTool?: (name: string) => void
  onDone?: (toolsUsed: string[]) => void
  onError?: (message: string) => void
}

export const CHAT_STREAM_INTERRUPTED = 'The reply was interrupted.'
export const THREAD_DELETED_CODE = 'THREAD_DELETED'

export class ChatThreadDeletedError extends Error {
  readonly code = THREAD_DELETED_CODE
  constructor(message = 'This conversation was deleted') {
    super(message)
    this.name = 'ChatThreadDeletedError'
  }
}

export function isChatThreadDeletedError(err: unknown): err is ChatThreadDeletedError {
  return (
    err instanceof ChatThreadDeletedError ||
    (err instanceof Error &&
      (err.name === 'ChatThreadDeletedError' ||
        (err as { code?: string }).code === THREAD_DELETED_CODE))
  )
}

async function throwIfThreadDeleted(res: Response): Promise<void> {
  if (res.status !== 410) return
  let message = 'This conversation was deleted'
  try {
    const body = (await res.clone().json()) as {
      code?: string
      message?: string | { code?: string; message?: string }
    }
    if (typeof body?.message === 'string' && body.message.trim()) {
      message = body.message
    } else if (body?.message && typeof body.message === 'object') {
      if (typeof body.message.message === 'string' && body.message.message.trim()) {
        message = body.message.message
      }
    } else if (typeof body?.code === 'string' && body.code === THREAD_DELETED_CODE) {
      // flat { code, message } without Nest wrapper
    }
  } catch {
    // ignore parse errors — still treat 410 as deleted
  }
  throw new ChatThreadDeletedError(message)
}

const BASE_URL = '/api/v1/admin'

async function authHeaders(): Promise<HeadersInit> {
  const token = localStorage.getItem('accessToken')
  return {
    Authorization: `Bearer ${token ?? ''}`,
    'Content-Type': 'application/json',
  }
}

export async function getPersonalThreadApi(): Promise<ChatThread> {
  const res = await fetch(`${BASE_URL}/chat/threads/personal`, { headers: await authHeaders() })
  if (!res.ok) throw new Error(`Failed to load personal thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function getPersonalHistoryApi(): Promise<ChatThreadSummary[]> {
  const res = await fetch(`${BASE_URL}/chat/threads/personal/history`, { headers: await authHeaders() })
  if (!res.ok) throw new Error(`Failed to load chat history (${res.status})`)
  return (await res.json()) as ChatThreadSummary[]
}

export async function archivePersonalThreadApi(): Promise<ChatThread> {
  const res = await fetch(`${BASE_URL}/chat/threads/personal/archive`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to archive personal thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function renamePersonalThreadApi(threadId: string, title: string): Promise<ChatThread> {
  const res = await fetch(`${BASE_URL}/chat/threads/personal/${encodeURIComponent(threadId)}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify({ title }),
  })
  if (!res.ok) throw new Error(`Failed to rename personal thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function deletePersonalThreadApi(threadId: string): Promise<ChatThread> {
  const res = await fetch(`${BASE_URL}/chat/threads/personal/${encodeURIComponent(threadId)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to delete personal thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function getSharedThreadApi(entityType: string, entityId: string): Promise<ChatThread> {
  const params = new URLSearchParams({ entityType, entityId })
  const res = await fetch(`${BASE_URL}/chat/threads/shared?${params}`, { headers: await authHeaders() })
  if (!res.ok) throw new Error(`Failed to load shared thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function deleteSharedThreadApi(threadId: string): Promise<ChatThread> {
  const res = await fetch(`${BASE_URL}/chat/threads/shared/${encodeURIComponent(threadId)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to delete shared thread (${res.status})`)
  return (await res.json()) as ChatThread
}

export async function getChatMessagesApi(threadId: string): Promise<ChatMessage[]> {
  const res = await fetch(`${BASE_URL}/chat/threads/${threadId}/messages`, {
    headers: await authHeaders(),
  })
  await throwIfThreadDeleted(res)
  if (!res.ok) throw new Error(`Failed to load chat messages (${res.status})`)
  return (await res.json()) as ChatMessage[]
}

export type ChatJiraIssue = {
  key: string
  summary: string
  url: string
}

export async function getJiraIssueApi(issueKey: string): Promise<ChatJiraIssue> {
  const res = await fetch(`${BASE_URL}/chat/jira/${encodeURIComponent(issueKey)}`, {
    headers: await authHeaders(),
  })
  if (!res.ok) throw new Error(`Failed to load Jira issue (${res.status})`)
  return (await res.json()) as ChatJiraIssue
}

/**
 * POST /chat SSE. Spend-on-send only — call only when the user submits.
 */
export async function streamChatApi(
  body: {
    threadId?: string
    message: string
    kind: 'personal' | 'shared'
    pageContext: ChatPageContext
    /** True when Retrying a turn whose user message was already persisted. */
    retry?: boolean
  },
  handlers: ChatStreamHandlers,
  signal?: AbortSignal
): Promise<void> {
  const res = await fetch(`${BASE_URL}/chat`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(body),
    signal,
  })
  await throwIfThreadDeleted(res)
  if (!res.ok || !res.body) {
    const text = await res.text().catch(() => '')
    throw new Error(text || `Chat request failed (${res.status})`)
  }

  if (!body.retry) {
    handlers.onUserPersisted?.()
  }

  const reader = res.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let receivedDone = false
  let streamError: string | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n\n')
    buffer = parts.pop() ?? ''
    for (const part of parts) {
      const lines = part.split('\n')
      let event = 'message'
      const dataLines: string[] = []
      for (const line of lines) {
        if (line.startsWith('event:')) event = line.slice(6).trim()
        else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
      }
      if (dataLines.length === 0) continue
      let data: Record<string, unknown> = {}
      try {
        data = JSON.parse(dataLines.join('\n')) as Record<string, unknown>
      } catch {
        continue
      }
      if (event === 'thread' && typeof data.threadId === 'string') {
        handlers.onThread?.(data.threadId, String(data.kind ?? ''))
      } else if (event === 'delta' && typeof data.text === 'string') {
        handlers.onDelta?.(data.text)
      } else if (event === 'tool' && typeof data.name === 'string') {
        handlers.onTool?.(data.name)
      } else if (event === 'done') {
        receivedDone = true
        handlers.onDone?.(Array.isArray(data.toolsUsed) ? (data.toolsUsed as string[]) : [])
      } else if (event === 'error' && typeof data.message === 'string') {
        streamError = data.message
        handlers.onError?.(data.message)
      }
    }
  }

  if (signal?.aborted) {
    const err = new Error('Aborted')
    err.name = 'AbortError'
    throw err
  }
  if (streamError) {
    throw new Error(streamError)
  }
  if (!receivedDone) {
    throw new Error(CHAT_STREAM_INTERRUPTED)
  }
}
